import { Icon } from "@iconify/react";
import { useState, useEffect, useContext } from "react";
import plusFill from "@iconify/icons-eva/plus-fill";
// material
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Box,
  LinearProgress,
  Chip
} from "@mui/material";
import { styled } from "@mui/material/styles";
// components
import Page from "../Page";
import Scrollbar from "../Scrollbar";
import UserMoreMenu from "./UserMoreMenu";
import { Context } from "../../context";
import axios from "../../util/axios";
import { useMutation } from "@tanstack/react-query";
import { deleteVehicleUrl } from "../../react-query/endpoints";
import AddVehicleModal from "./AddVehicleModal";

// ---- CMS-UI Styled Components (matching BaseTable.js exactly) ----
const StyledTableContainer = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  backgroundColor: '#ffffff',
  border: '1px solid rgba(0,0,0,0.05)',
  overflow: 'hidden',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
  },
}));

// Column definitions
const TABLE_HEAD = [
  { id: "slno", label: "Sl No", align: "center", width: 70 },
  { id: "make", label: "Make", align: "left" },
  { id: "model", label: "Model", align: "left" },
  { id: "charger", label: "Charger", align: "left" },
  { id: "color", label: "Color", align: "left" },
  { id: "status", label: "Status", align: "center" },
  { id: "actions", label: "Actions", align: "right", width: 80 },
];

export default function MyVehicle() {
  const { user } = useContext(Context);
  const { userDetail } = user;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const fetchUsersVehicleList = () => {
    (async function () {
      setLoading(true);
      try {
        const response = await axios(
          `${process.env.REACT_APP_API_END_POINT}user/list-vehicle?user=${userDetail._id}`,
          { method: "get" }
        );
        setUserList(response.data.data || []);
      } catch (error) {
        console.error(error.response);
      } finally {
        setLoading(false);
      }
    })();
  };

  const deleteVehicle = async ({ vehicleId }) => {
    const endpoint = deleteVehicleUrl(vehicleId);
    return await axios(endpoint, { method: "delete" });
  };

  const deleteMyVehicle = useMutation(deleteVehicle, {
    onSuccess: () => {
      fetchUsersVehicleList();
    },
  });

  useEffect(() => {
    if (!userDetail?._id) return;
    fetchUsersVehicleList();
  }, [userDetail]);

  const handleDeleteBtnClick = (vehicleId) => {
    deleteMyVehicle.mutate({ vehicleId });
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const addVehicleHandler = () => {
    setSelectedVehicle(null);
    setShowAddVehicle(true);
  };
  const editVehicleHandler = (vehicleToEdit) => {
    setSelectedVehicle(vehicleToEdit);
    setShowAddVehicle(true);
  };
  const AddVehicleModalClose = () => {
    fetchUsersVehicleList();
    setShowAddVehicle(false);
  };

  const paginatedData = userList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Page title="My Vehicles | ChargeMiles Dashboard">
      <Container maxWidth="lg" sx={{ mt: { xs: 8, md: 10 }, mb: 10 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <Typography variant="h4" fontWeight={600}>My Vehicles</Typography>
          <Button
            variant="contained"
            startIcon={<Icon icon={plusFill} />}
            onClick={addVehicleHandler}
            sx={{
              background: (theme) => `linear-gradient(135deg, var(--app-color) 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: (theme) => `0 8px 16px 0 ${theme.palette.primary.main}3D`,
              '&:hover': {
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, var(--app-color) 100%)`,
              },
            }}
          >
            Add Vehicle
          </Button>
        </Stack>

        {!showAddVehicle && (
          <StyledTableContainer>
            {/* CMS-UI style linear loading bar */}
            {loading && (
              <LinearProgress
                sx={{
                  height: 3,
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, var(--app-color), #103996)',
                  },
                }}
              />
            )}

            <TableContainer>
              <Table>
                {/* ---- Header: matches BaseTable .MuiDataGrid-columnHeader ---- */}
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: '#f8f9fa !important',
                      borderBottom: '2px solid #e9ecef !important',
                    }}
                  >
                    {TABLE_HEAD.map((col) => (
                      <TableCell
                        key={col.id}
                        align={col.align}
                        sx={{
                          width: col.width || 'auto',
                          fontWeight: '600 !important',
                          fontSize: '0.875rem !important',
                          color: '#495057 !important',
                          backgroundColor: '#f8f9fa !important',
                          borderBottom: '2px solid #e9ecef !important',
                          padding: '12px 16px !important',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                {/* ---- Body: matches BaseTable .MuiDataGrid-row / .MuiDataGrid-cell ---- */}
                <TableBody>
                  {!loading && paginatedData.length === 0 && (
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 6 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No vehicles yet
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          Add your first vehicle to get started!
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {!loading &&
                    paginatedData.map((row, index) => {
                      const { _id, make, model, charger, color, status } = row;
                      const serialNo = page * rowsPerPage + index + 1;
                      const isEvenRow = index % 2 === 1;

                      return (
                        <TableRow
                          hover
                          key={_id}
                          sx={{
                            minHeight: 'auto !important',
                            maxHeight: 'none !important',
                            transition: 'background-color 0.2s ease',
                            backgroundColor: isEvenRow
                              ? 'rgba(0,0,0,0.01)'
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.04) !important',
                            },
                            '& .MuiTableCell-root': {
                              whiteSpace: 'normal !important',
                              wordWrap: 'break-word !important',
                              lineHeight: '1.4 !important',
                              padding: '14px 16px !important',
                              borderBottom: '1px solid rgba(0,0,0,0.05) !important',
                              fontSize: '0.875rem',
                              color: 'rgba(0,0,0,0.87)',
                            },
                          }}
                        >
                          <TableCell align="center">
                            <Typography variant="body2" color="text.secondary">
                              {serialNo}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {make}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{model}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{charger}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{color}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={status === 1 ? "Active" : "Inactive"}
                              size="small"
                              color={status === 1 ? "success" : "error"}
                              variant="outlined"
                              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <UserMoreMenu
                              editVehicleHandler={editVehicleHandler}
                              onDeleteHandler={handleDeleteBtnClick}
                              selectedVehicle={row}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ---- Footer: matches BaseTable .MuiDataGrid-footerContainer ---- */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={userList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid #e9ecef !important',
                backgroundColor: '#f8f9fa !important',
                minHeight: '52px !important',
                '& .MuiTablePagination-root': {
                  color: '#495057 !important',
                },
              }}
            />
          </StyledTableContainer>
        )}

        {showAddVehicle && (
          <AddVehicleModal
            open={showAddVehicle}
            onClose={AddVehicleModalClose}
            selectedVehicle={selectedVehicle}
          />
        )}
      </Container>
    </Page>
  );
}
